import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/ApiProvider';
import type {
  CalcAdjustPublishedValueInput,
  CalcListParams,
  CalcPrepareInput,
} from '../../lib/api';

/**
 * Query keys for the LOT 6B calculations. Everything lives under `['calculations', dossierId]`
 * so any calc mutation can invalidate the dossier's runs in one shot — the server recomputes and
 * re-hashes, the web only re-reads the authoritative values.
 */
export const calcKeys = {
  all: ['calculations'] as const,
  dossier: (dossierId: string) => ['calculations', dossierId] as const,
  list: (dossierId: string, params: CalcListParams) =>
    ['calculations', dossierId, 'list', params] as const,
  run: (id: string) => ['calculations', 'run', id] as const,
};

/** List calc runs of a dossier (history section). */
export function useCalculations(dossierId: string, params: CalcListParams = {}, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: calcKeys.list(dossierId, params),
    queryFn: () => api.listCalculations(dossierId, params),
    enabled,
  });
}

/** Read a single calc run (full detail). */
export function useCalculation(id: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: calcKeys.run(id ?? ''),
    queryFn: () => api.getCalculation(id ?? ''),
    enabled: enabled && id !== null && id !== '',
  });
}

/** Prepare: freezes the intent; the server returns a PREPARE (or BLOQUE) run. */
export function usePrepareCalculation(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CalcPrepareInput) => api.prepareCalculation(dossierId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: calcKeys.dossier(dossierId) }),
  });
}

/** Execute a prepared run: the server runs the engine and persists the hashed result. */
export function useExecuteCalculation(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ calcRunId, idempotencyKey }: { calcRunId: string; idempotencyKey?: string }) =>
      api.executeCalculation(dossierId, calcRunId, idempotencyKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: calcKeys.dossier(dossierId) }),
  });
}

/** Review / Validate / Finalize workflow transitions (server-gated). */
export function useReviewCalculation(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.reviewCalculation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: calcKeys.dossier(dossierId) }),
  });
}

export function useValidateCalculation(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.validateCalculation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: calcKeys.dossier(dossierId) }),
  });
}

export function useFinalizeCalculation(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.finalizeCalculation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: calcKeys.dossier(dossierId) }),
  });
}

/** Adjust the published (final) value — mandatory justification, server-decided. */
export function useAdjustPublishedValue(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CalcAdjustPublishedValueInput }) =>
      api.adjustPublishedValue(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: calcKeys.dossier(dossierId) }),
  });
}

/** Recalculate: a new prepare payload; the produced run supersedes the source run. */
export function useRecalculateCalculation(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CalcPrepareInput }) =>
      api.recalculateCalculation(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: calcKeys.dossier(dossierId) }),
  });
}
